/**
 * DE-130 — tiny generic hook for table sort + search shared by Dashboard
 * and OpsDevices. Local state only; no URL persistence (could add later via
 * useSearchParams if needed).
 */
import { useMemo, useState } from 'react';

export interface SortState<K extends string> {
  key: K;
  dir: 'asc' | 'desc';
}

export function useTableSearch<T, K extends string>(
  rows: T[] | undefined,
  options: {
    // Fields the search box matches against. Each accessor returns the
    // searchable text (or null if missing — null treated as empty string).
    searchAccessors: Array<(row: T) => string | null | undefined>;
    // Column-keyed comparators for sort. The default sort + initial sort
    // are also driven by these keys.
    sortAccessors: Record<K, (row: T) => string | number | null>;
    initialSort?: SortState<K>;
  },
) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState<K> | null>(options.initialSort ?? null);

  const filteredSortedRows = useMemo(() => {
    if (!rows) return [];
    const needle = search.trim().toLowerCase();
    let out = rows;
    if (needle) {
      out = rows.filter((r) =>
        options.searchAccessors.some((acc) => {
          const v = acc(r);
          return typeof v === 'string' && v.toLowerCase().includes(needle);
        }),
      );
    }
    if (sort) {
      const cmp = options.sortAccessors[sort.key];
      out = [...out].sort((a, b) => {
        const av = cmp(a);
        const bv = cmp(b);
        // Nulls last regardless of direction.
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        if (av < bv) return sort.dir === 'asc' ? -1 : 1;
        if (av > bv) return sort.dir === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return out;
  }, [rows, search, sort, options.searchAccessors, options.sortAccessors]);

  function toggleSort(key: K) {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return null; // third click clears sort
    });
  }

  return {
    search,
    setSearch,
    sort,
    setSort,
    toggleSort,
    rows: filteredSortedRows,
  };
}

/** Render a small ↑↓ indicator next to a sortable column header. */
export function sortIndicator<K extends string>(
  sort: SortState<K> | null,
  key: K,
): string {
  if (!sort || sort.key !== key) return '';
  return sort.dir === 'asc' ? ' ↑' : ' ↓';
}
